import React, { createContext, useContext, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import {
  CardPublishState,
  useCoursePublishState,
} from 'src/hooks/useCoursePublishState'

const CoursePublishContext = createContext(null)

/**
 * Shares one course's publish state across the editor.
 *
 * The top bar and the table of contents render two halves of the same picture — the course badge
 * and the per-page indicators — but live in different parts of the tree. Fetching separately would
 * let them disagree after a publish, so they read from a single instance here.
 */
export const CoursePublishProvider = ({ children }) => {
  const courseId = useSelector(state => state.course.course?.id)
  const value = useCoursePublishState(courseId)

  // The editor saves as you type, so publish state goes stale the moment an edit lands. Refetch
  // when a save finishes — otherwise a page you just edited keeps looking clean until reload,
  // which is exactly the confusion this feature exists to remove.
  //
  // Two signals, because they cover different paths: editorLoading tracks the body editor's
  // autosave, while cardPatchCount rises on every successful card patch — including the title,
  // which is saved on blur and never touches editorLoading, the completion page's badge and
  // feedback settings, which are patched as settings rather than as card content, and a quiz
  // page's options, which are their own endpoints and so touch neither of the other two.
  const saving = useSelector(state => state.card.editorLoading)
  const patchCount = useSelector(state => state.card.cardPatchCount)
  const wasSaving = useRef(false)
  const lastPatchCount = useRef(patchCount)
  const { refresh } = value

  useEffect(() => {
    const saveFinished = wasSaving.current && !saving
    const cardPatched = patchCount !== lastPatchCount.current

    if (saveFinished || cardPatched) {
      refresh()
    }

    wasSaving.current = saving
    lastPatchCount.current = patchCount
  }, [saving, patchCount, refresh])

  return (
    <CoursePublishContext.Provider value={value}>
      {children}
    </CoursePublishContext.Provider>
  )
}

/**
 * Returns null outside the editor, so callers can render nothing rather than guess a state.
 */
export const useCoursePublish = () => useContext(CoursePublishContext)

/**
 * Publish state of a single page, or null when it is not known yet.
 */
export const usePagePublishState = cardId => {
  const context = useContext(CoursePublishContext)

  if (!context || !cardId) {
    return null
  }

  return (
    context.pages.find(page => String(page.cardId) === String(cardId)) ?? null
  )
}

export { CardPublishState }
export default CoursePublishContext
